import React, { ComponentProps, FC } from 'react';

import { Button, OnClick } from '@rbilabs/components-library/build/components/button';
import { Icon } from '@rbilabs/components-library/build/components/icon';
import { Box, Container } from '@rbilabs/components-library/build/components/layout';
import { Subtitle2 } from '@rbilabs/components-library/build/components/typography';
import { useIdCounter } from '@rbilabs/components-library/build/hooks/use-id-counter';
import { getThemeColor } from '@rbilabs/components-library/build/utils';
import { noop } from 'lodash';
import styled from 'styled-components';

import { LoadingAnimation } from 'components/loading-animation';
import { StoreCardButtonOptions } from 'state/launchdarkly/variations';

const IconButton = styled(Button)`
  div > svg,
  div > svg[data-legacy-icon] {
    height: 100%;
    width: 100%;
  }
`;

const StyledSubtitle2 = styled(Subtitle2)`
  text-align: center;
`;

const Text = styled.span<{ disabled?: boolean }>`
  color: ${({ disabled, theme }) =>
    getThemeColor(theme, disabled ? 'text-disabled' : 'text-default')};
`;

const LoadingIcon = styled(LoadingAnimation)`
  width: 2rem;
  height: 2rem;

  span {
    width: 0.5rem;
    height: 0.5rem;
  }
`;

interface IStoreActionsButtonProps {
  disabled?: boolean;
  icon: ComponentProps<typeof Icon>['icon'];
  id: StoreCardButtonOptions;
  loading?: boolean;
  onClick?: OnClick;
  text: string;
}

export const StoreActionsButton: FC<IStoreActionsButtonProps> = ({
  disabled = false,
  onClick = noop,
  icon,
  id,
  text,
  loading = false,
}) => {
  const textId = `store-action-text-${id}`;
  const buttonId = `store-action-button-${id}`;
  const storeActionTextId = useIdCounter(textId);
  const storeActionButtonId = useIdCounter(buttonId);

  return (
    <Container data-testid="store-action-button-container" align="center" margin="0 1rem">
      <IconButton
        data-testid={buttonId}
        aria-labelledby={storeActionTextId}
        disabled={loading || disabled}
        id={storeActionButtonId}
        onClick={onClick}
        startIcon={
          loading ? (
            <LoadingIcon fillColor={Styles.color.grey.two} />
          ) : (
            <Icon aria-hidden icon={icon} color="icon-button-primary" height="2rem" width="2rem" />
          )
        }
      />
      <Box height="0.25rem" minHeight="0.25rem" maxHeight="0.25rem" />
      <StyledSubtitle2>
        <Text id={storeActionTextId} data-testid={textId} disabled={disabled}>
          {text}
        </Text>
      </StyledSubtitle2>
    </Container>
  );
};
